import React, { useState, useEffect, useMemo } from 'react';
import API from '../../../utils/API';
import jobsAPI from '../../../utils/API/jobsAPI';
import countriesList from './countriesList';
import { useJobs } from '../../../utils/hooks/useJobs';
import { useAuth } from '../../../utils/Auth';
import Collapse from 'react-bootstrap/Collapse';
import styles from './Jobs.module.css';

export default function Jobs() {
    const { vcardObj, userObj } = useAuth();
    const { uid } = userObj;
    const { resume_link, show_resume } = vcardObj;

    const { active, jobs } = useJobs();

    const [ show, setShow ] = useState(false);
    const [ state, setState ] = useState({
        // required
        country: 'United States',
        // required
        zip: '',
        job_board: false,
        show_resume: show_resume || false,
        full_time: false,
        part_time: false,
        contract: false,
        file: {
            data: null,
            name: resume_link || 'No File Selected',
        },
    });
    const [ loading, setLoading ] = useState(false);
    const [ messages, setMessages ] = useState({
        jobs: {
            success: '',
            error: '',
        },
        file: {
            success: '',
            error: ''
        }
    });

    useEffect(() => {
        if (jobs) {
            setState(prev => ({
                ...prev,
                country: jobs.country || 'United States',
                zip: jobs.location || '',
                job_board: jobs.job_board || false,
                show_resume: show_resume || false,
                full_time: jobs.full_time || false,
                part_time: jobs.part_time || false,
                contract: jobs.contract || false,
            }));
        };
    },[jobs, show_resume])

    const handleAllowJobs = async (bool) => {
        setLoading(true);
        try {
            if (!bool) {
                await API.updateVcardObject({ show_resume: false });
                await jobsAPI.updateJobSettings({ job_board: false });
                setState({
                    country: 'United States',
                    zip: '',
                    job_board: false,
                    show_resume: show_resume || false,
                    full_time: false,
                    part_time: false,
                    contract: false,
                    file: {
                        data: null,
                        name: resume_link || 'No File Selected',
                    },
                })
            }
    
            await API.updateUserObject({ jobs: bool });
        } catch (err) {
            console.log('Error updating allowing jobs', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveJobSettings = async () => {
        setLoading(true);
        try {
            if (state.country && state.zip?.length > 4) {
                if (state.file.data && uid && Math.trunc((state.file.data.size / 1024) / 1000) < 10) {
                    await API.uploadResume(uid, state.file.data, state.file.name);
                    setMessages(prev => ({...prev, file: {...prev.file, success: 'Upload Success!'}}))
                }
                await jobsAPI.updateJobSettings({
                    country: state.country,
                    location: `${state.zip}`,
                    job_board: state.job_board,
                    full_time: state.full_time,
                    part_time: state.part_time,
                    contract: state.contract
                });
                await API.updateVcardObject({resume_link: state.file.data ? `${state.file.name}` : resume_link ? resume_link : null, show_resume: state.show_resume});
                setMessages(prev => ({...prev, jobs: {...prev.jobs, success: 'Job Settings Saved!'}}));
            } else {
                setMessages(prev => ({...prev, jobs: {...prev.jobs, error: 'Please fill out all fields.'}}));
            }
        } catch (err) {
            // UPDATE WITH SPECIFIC ERROR HANDLING
            // DONT FORGET THIS!!!!!!
            setMessages(prev => ({...prev, jobs: {...prev.jobs, error: 'Generic Error'}}));
        } finally {
            setLoading(false);
            handleRemoveFile();
        };
    };

    const handleFile = (e) => {
        const file = e.target.files[0];
        if (file && Math.trunc((file.size / 1024) / 1000) > 10) {
            setMessages(prev => ({...prev, file: { success: '', error: 'File size too large.'}}))
            e.target.value = '';
        } else if (file) {
            setState(prev => ({...prev, file: { data: file, name: file.name}}))
        } else {
            setState(prev => ({...prev, file: { data: null, name: resume_link ? resume_link : 'No File Selected'}}))
            e.target.value = ''
        }
    };

    const handleRemoveFile = () => {
        setState(prev => ({...prev, file: { data: null, name: resume_link ? resume_link : 'No File Selected'}}));
        const input = document.querySelector('#resume-input');
        input.value = '';
    };

    useEffect(() => {
        // handles error messages
        if (messages.jobs.success || messages.jobs.error) {
            const timer = setTimeout(() => {
                setMessages(prev => ({...prev, jobs: { success: '', error: ''}}));
            }, 2500);

            return () => clearTimeout(timer);
        }

        
    }, [messages.jobs.success, messages.jobs.error]);
    
    useEffect(() => {
        if (messages.file.success || messages.file.error) {
            const timer = setTimeout(() => {
                setMessages(prev => ({...prev, file: { success: '', error: ''}}));
            }, 2500);
    
            return () => clearTimeout(timer);
        }
    }, [messages.file.success, messages.file.error])

    const handleSubtitle = useMemo(() => {
        return active ? 'Upload your resume to get some attention' : 'Broaden your career prospects!';
    },[active]);

    const handleReturn = () => {
        if (active) {
            return (
                <div className={styles.jobs}>
                    <div className={styles.jobsTitle}>Location</div>
                    <div className={styles.inputGroup} style={{flexDirection: 'column'}}>
                        <label htmlFor='country-select' className={styles.label}>Country of Residence</label>
                        <select 
                            className={styles.select} 
                            id='country-select' 
                            value={state.country}
                            onChange={(e) => setState(prev => ({...prev, country: e.target.value}))}
                            placeholder='Select a Country'
                            required
                        >
                            {countriesList.map((country, i) => (
                                <option key={i} value={country}>{country}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.inputGroup} style={{flexDirection: 'column'}}>
                        <label htmlFor='zip-code' className={styles.label}>Zip Code</label>
                        <input 
                            type='text'
                            className={styles.input}
                            id='zip-code'
                            value={state.zip}
                            placeholder='Enter Zip Code'
                            onChange={(e) => setState(prev => ({...prev, zip: e.target.value}))}
                            required
                        />
                    </div>
                    <div className={styles.inputGroup}>
                        <input 
                            type='checkbox' 
                            id='job-findable' 
                            className={styles.checkbox} 
                            checked={state.job_board} 
                            onChange={(e) => setState(prev => ({...prev, job_board: e.target.checked}))}
                        />
                        <label htmlFor='job-findable' className={styles.checkboxLabel}>Businessess may contact me with job opportunities.</label>
                    </div>
                    <div className={styles.jobsTitle}>Resume</div>
                    <div className={styles.inputGroup} style={{flexDirection: 'column', marginBottom: '0'}}>
                        <label htmlFor='resume-input' className={state.file.data ? `${styles.resumeBtn} ${styles.hasFile}` : styles.resumeBtn}>
                            <i className='far fa-cloud-arrow-up me-1' />
                            Upload Resume
                            <span className={styles.fileCheck}><i className='far fa-circle-check' /></span>
                        </label>
                        <input className={styles.resumeInput} type='file' id='resume-input' accept='application/pdf' disabled={!active} onChange={handleFile} />
                        <div className={styles.resumeName}>
                            {messages.file.success ? 
                                <span style={{color: 'green'}}>{messages.file.success}</span>
                            : messages.file.error ?
                                <span style={{color: 'red'}}>{messages.file.error}</span>
                            : <div>
                                Current: {state.file.name}
                                {state.file.data ? <button className={styles.removeFile} onClick={handleRemoveFile}><i className='far fa-xmark' /></button> : <></>}
                            </div>}
                        </div>
                    </div>
                    <div className={styles.inputGroup}>
                        <input
                            type='checkbox'
                            id='show-resume'
                            className={styles.checkbox}
                            checked={state.show_resume}
                            onChange={(e) => setState(prev => ({...prev, show_resume: e.target.checked}))}
                        />
                        <label htmlFor='show-resume' className={styles.checkboxLabel}>Show Resume to Employers</label>
                    </div>
                    <div className={styles.jobsTitle}>Save</div>
                    <div className={styles.inputGroup}>
                        <button className={styles.saveBtn} onClick={handleSaveJobSettings} disabled={!state.country || !state.zip || loading}>{loading ? <i className='fa-solid fa-spinner fa-spin-pulse' /> : 'Save Jobs Settings'}</button>
                        {messages.jobs.success ? <div className={styles.saveMessage} style={{color: 'green'}}>{messages.jobs.success}</div>
                        : messages.jobs.error ? <div className={styles.saveMessage} style={{color: 'red'}}>{messages.jobs.error}</div>
                        : <></>}
                    </div>
                    <div className={styles.jobsTitle}>Opt Out</div>
                    <div className={styles.inputGroup}>
                        <button id='opt-out-btn' className={styles.saveBtn} onClick={() => handleAllowJobs(false)} disabled={loading}>{loading ? <i className='fa-solid fa-spinner fa-spin-pulse' /> : 'Disable Jobs'}</button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={styles.optIn}>
                    <div className={styles.optInWords}>Opt in to BitSignal Jobs</div>
                    <button id='as-job-opt-in-btn' className={styles.optInBtn} onClick={() => handleAllowJobs(true)} disabled={loading}>{loading ? <i className='fa-solid fa-spinner fa-spin-pulse' /> : 'Activate'}</button>
                </div>
            );
        }
    };


    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.headerInner}>
                    <div className={styles.title}>Jobs</div>
                    <div className={styles.subtitle}>{handleSubtitle}</div>
                </div>
                <button className={!show ? styles.collapseBtn : `${styles.collapseBtn} ${styles.open}`} onClick={() => setShow(!show)}>
                    <i className='fas fa-chevron-down' />
                </button>
            </div>
            <Collapse in={show}>
                <div className={styles.position}>
                    <div className={styles.content}>
                        {handleReturn()}
                    </div>
                </div>
            </Collapse>
        </div>
    );
};